import { useMemo, useState } from "react";
import { FileSignature, FileText } from "lucide-react";
import { money, QUOTE_STATUS_COLORS, calcCommission, effectiveRate, calcAgentCommission } from "../lib/finance";
import { CompanyName } from "../lib/companies";
import { Empty } from "./common";
import { QuoteCompareBar } from "./QuoteCompareBar";

const MONTHS = ["Ocak", "Şubat", "Mart", "Nisan", "Mayıs", "Haziran", "Temmuz", "Ağustos", "Eylül", "Ekim", "Kasım", "Aralık"];
const fmt = (s) => (s ? new Date(`${String(s).slice(0, 10)}T00:00:00`).toLocaleDateString("tr-TR") : "—");

function Amounts({ x, customer, branches, settings }) {
  const personal = calcCommission(x.premium, effectiveRate(x, branches), settings.netDivisor, settings.personalShare, x.netPremium, x.status === "iptal" ? x.cancellationRefund : null).personal;
  const agent = Number(customer?.commissionRate) > 0 ? calcAgentCommission({ ...x, customer: customer.name }, [customer], branches, settings).agent : 0;
  return (
    <div className="ctl-amts" data-testid={`timeline-amounts-${x.id}`}>
      <div><small>Brüt prim</small><b>{money(x.premium || 0)}</b></div>
      <div><small>Kişisel</small><b className="commission">{money(personal)}</b></div>
      <div><small>Acente</small><b>{agent > 0 ? money(agent) : "—"}</b></div>
    </div>
  );
}

export function CustomerTimeline({ onQuotesSent, onDocCreated, policies, quotes, customer, branches, settings, onOpenPolicy, onOpenQuote }) {
  const [kind, setKind] = useState("all");
  const [year, setYear] = useState("");
  const [month, setMonth] = useState("");
  const [sel, setSel] = useState([]);
  const items = useMemo(() => [
    ...policies.map((p) => ({ type: "policy", date: String(p.start || p.createdAt || "").slice(0, 10), item: p })),
    ...quotes.map((q) => ({ type: "quote", date: String(q.createdAt || "").slice(0, 10), item: q })),
  ].sort((a, b) => b.date.localeCompare(a.date)), [policies, quotes]);
  const years = useMemo(() => [...new Set(items.map((i) => i.date.slice(0, 4)).filter(Boolean))].sort().reverse(), [items]);
  const shown = items.filter((i) => (kind === "all" || i.type === kind) && (!year || i.date.startsWith(year)) && (!month || i.date.slice(5, 7) === month));
  return (
    <div className="ctl" data-testid="customer-timeline">
      <div className="ctl-filters">
        <div className="segmented" data-testid="timeline-kind">
          {[["all", "Tümü"], ["quote", "Teklif"], ["policy", "Poliçe"]].map(([k, l]) => (
            <button key={k} type="button" className={kind === k ? "active" : ""} onClick={() => setKind(k)} data-testid={`timeline-kind-${k}`}>{l}</button>
          ))}
        </div>
        <select value={month} onChange={(e) => setMonth(e.target.value)} data-testid="timeline-month">
          <option value="">Tüm aylar</option>
          {MONTHS.map((m, i) => <option key={m} value={String(i + 1).padStart(2, "0")}>{m}</option>)}
        </select>
        <select value={year} onChange={(e) => setYear(e.target.value)} data-testid="timeline-year">
          <option value="">Tüm yıllar</option>
          {years.map((y) => <option key={y} value={y}>{y}</option>)}
        </select>
        <span className="ctl-count" data-testid="timeline-count">{shown.length} kayıt</span>
      </div>
      <QuoteCompareBar selected={sel.map((id) => quotes.find((x) => x.id === id)).filter(Boolean)} onClear={() => setSel([])}
        onSent={(ids, data) => { onQuotesSent?.(ids); onDocCreated?.(data?.doc); setSel([]); }} />
      {shown.length === 0 ? <Empty text="Seçilen döneme ait teklif veya poliçe yok" /> : (
        <ul className="ctl-list">
          {shown.map(({ type, item: x }) => (
            <li key={`${type}-${x.id}`} className="ctl-li">
              {type === "quote" && <input type="checkbox" className="ctl-check" checked={sel.includes(x.id)} onChange={() => setSel((s) => (s.includes(x.id) ? s.filter((i) => i !== x.id) : [...s, x.id]))} data-testid={`timeline-select-${x.id}`} aria-label="Karşılaştırmaya ekle" />}
              <button type="button" className="ctl-row" onClick={() => (type === "policy" ? onOpenPolicy(x) : onOpenQuote(x))} data-testid={`timeline-${type}-${x.id}`}>
                <span className={`ctl-tag ${type}`}>{type === "policy" ? <FileText size={12} /> : <FileSignature size={12} />}{type === "policy" ? "Poliçe" : "Teklif"}</span>
                <div className="ctl-main">
                  <strong>{x.branch || "—"}{x.plate ? <span className="plate-chip">{x.plate}</span> : null}</strong>
                  <span>{type === "policy" ? `${x.policeNo || "No yok"} · ${fmt(x.start)} — ${fmt(x.end)}` : `${fmt(x.createdAt)}${x.expiryDate ? ` · geçerlilik ${fmt(x.expiryDate)}` : ""}`}</span>
                </div>
                <div className="ctl-company">{x.company ? <CompanyName name={x.company} size={18} /> : <em className="dim">—</em>}</div>
                {type === "quote" ? <span className={`badge badge-${QUOTE_STATUS_COLORS[x.status] || "slate"}`}>{x.status}</span> : <span className={`badge badge-${(x.status || "aktif") === "iptal" ? "red" : "green"}`}>{(x.status || "aktif") === "iptal" ? "İptal" : "Aktif"}</span>}
                <Amounts x={x} customer={customer} branches={branches} settings={settings} />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
